/* eslint-disable jira/filename-case */
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

let ssrFailedMarker = false;
let failedMarkers = new Set<string>();

autoBeforeEachTest(() => {
	ssrFailedMarker = false;
	failedMarkers = new Set();
});

/**
 * @internal only for local usage by rendering-guard and by `markSsrFailuresFromHtml` below
 */
export const markSSRFailure = (fallbackId: string | undefined): void => {
	ssrFailedMarker = true;
	if (fallbackId != null) {
		failedMarkers.add(fallbackId);
	}
};

/**
 * reports if SSR failure was reported
 */
export const hasFailedSSR = (): boolean => ssrFailedMarker;
export const getFailedMarkers = (): Set<string> => failedMarkers;

/**
 * Data attribute tagging an SSR fallback marker in the streamed HTML (emitted by rendering-guard's
 * `ReportSSRFailOnUse` as an inert `<template>` inside the Suspense fallback, under streaming).
 *
 * The marker is present in the settled HTML whenever a boundary shipped its fallback. Whether that
 * fallback was *terminal* (a real SSR failure) or was later replaced by resolved children is not
 * something the marker can tell us on its own - see {@link markSsrFailuresFromHtml}.
 */
export const SSR_FALLBACK_MARKER_ATTRIBUTE = 'data-jira-ssr-fallback';

const collectResolvedBoundaryIds = (html: string): Set<string> => {
	const COMPLETED_BOUNDARY_CALL = /\$RC\("(B:[^"]+)"/g;
	const ids = new Set<string>();
	let match = COMPLETED_BOUNDARY_CALL.exec(html);
	while (match !== null) {
		ids.add(match[1]);
		match = COMPLETED_BOUNDARY_CALL.exec(html);
	}
	return ids;
};

/**
 * Derives per-boundary streaming SSR failures purely from the settled SSR HTML and records them via
 * {@link markSSRFailure}. Call once the streaming render has fully settled, before reading the markers
 * for metrics. All of the failure-state logic lives here; `ReportSSRFailOnUse` only emits the inert
 * fallback marker ({@link SSR_FALLBACK_MARKER_ATTRIBUTE}) into the Suspense fallback.
 *
 * The rule: a fallback marker is a *failure* iff its enclosing `B:n` boundary never appears in a
 * `$RC(...)` completion call. A boundary with no fallback marker is never considered — the success
 * paths emit no marker at all. Scenario by scenario:
 *
 *   | Boundary outcome                     | What lands in the settled HTML                       | Verdict |
 *   | ------------------------------------ | ---------------------------------------------------- | ------- |
 *   | Rendered successfully (no suspend)   | children inline, no fallback → no marker             | success |
 *   | Suspended then resolved (onAllReady) | children inline, no fallback → no marker             | success |
 *   | Suspended then resolved (streaming)  | fallback marker + a `$RC("B:n",…)` for its boundary  | success |
 *   | Fallback only, never resolved        | fallback marker, no `$RC` (aborted boundary → `$RX`) | failure |
 *
 * React 18 Fizz protocol this reads (implementation details of `react-dom/server`, pinned by the
 * `rendering-guard` "upgrade canary" tests in `streamingInstrumentation.test.tsx`):
 * - Resolved-before-flush boundary: `<!--$-->children<!--/$-->` (inline, no fallback; `<!--$!-->` is
 *   the errored-inline variant). This is the entire output in the buffered `onAllReady` path prod uses.
 * - Still-suspended-at-flush boundary: `<!--$?--><template id="B:n"></template>{fallback}<!--/$-->`,
 *   carrying our marker; `B:n` identifies the boundary.
 * - Boundary that resolves during streaming: a `$RC("B:n","S:n")` script swaps the fallback for the
 *   children on the client. The fallback markup (and our marker) persists in the bytes after the swap,
 *   so `$RC` — keyed by the same `B:n` — is the only reliable "resolved" signal.
 * - Aborted/never-resolved boundary: a `$RX("B:n",…)` script and no `$RC`.
 *
 * Why key off `$RC` and not just marker presence/order (a tempting simplification, e.g. also emit a
 * "success" marker in the children): it does not work. React flushes a boundary's resolved-so-far
 * children into a `<div hidden id="S:n">` completion segment while the boundary is still pending, and
 * that segment persists even if the boundary is later aborted — so a children-branch marker appears in
 * the settled HTML even for a boundary that never resolved (verified: an aborted boundary's children
 * marker sits alongside `$RX` and no `$RC`; two siblings both emit children markers though only one
 * resolved). The segment is inert until `$RC` commits it, so neither marker presence nor document order
 * distinguishes resolved from terminal fallback — only `$RC` does. Hence the marker lives only in the
 * fallback.
 */
export const markSsrFailuresFromHtml = (html: string): void => {
	// Best-effort monitoring — a bug in the derivation below must never crash the SSR render, so it is
	// caught here. It is logged (not swallowed) so a regression is visible, but only the error *type*
	// is emitted — never the message or stack, which could contain customer data.
	try {
		const STREAM_TOKEN =
			/<!--\$[?!]?-->|<!--\/\$-->|<template id="(B:[^"]+)">|data-jira-ssr-fallback="([^"]*)"/g;
		const SUSPENSE_CLOSE = '<!--/$-->';
		const resolvedBoundaryIds = collectResolvedBoundaryIds(html);

		// Walk the Suspense comment structure as a stack so each fallback marker is attributed to the
		// `B:n` id of the boundary that encloses it, even when boundaries nest.
		const boundaryStack: Array<string | null> = [];
		let token = STREAM_TOKEN.exec(html);
		while (token !== null) {
			const [matched, boundaryId, markerKey] = token;
			if (boundaryId != null) {
				// `<template id="B:n">` immediately follows the open comment of the boundary it belongs to.
				const top = boundaryStack.length - 1;
				if (top >= 0 && boundaryStack[top] == null) {
					boundaryStack[top] = boundaryId;
				}
			} else if (markerKey != null) {
				let enclosingBoundaryId: string | null = null;
				for (let i = boundaryStack.length - 1; i >= 0; i--) {
					if (boundaryStack[i] != null) {
						enclosingBoundaryId = boundaryStack[i];
						break;
					}
				}
				if (enclosingBoundaryId == null || !resolvedBoundaryIds.has(enclosingBoundaryId)) {
					// A fallback with no id serializes to `data-jira-ssr-fallback=""` (emit side uses
					// `fallbackId ?? ''`). Reverse that here so an empty id is treated as "no id" -
					// matching the legacy path, which called `markSSRFailure(undefined)` and never added
					// an empty string to the failed-marker set. The failure is still recorded either way.
					markSSRFailure(markerKey || undefined);
				}
			} else if (matched === SUSPENSE_CLOSE) {
				boundaryStack.pop();
			} else {
				boundaryStack.push(null);
			}
			token = STREAM_TOKEN.exec(html);
		}
	} catch (error) {
		// oxlint-disable-next-line no-console
		console.error(
			`markSsrFailuresFromHtml: SSR failure derivation threw ${
				error instanceof Error ? error.name : typeof error
			}`,
		);
	}
};
